import * as THREE from 'three';
import type { Collider } from '../physics/Collision';
import { segmentHitsCircle } from '../physics/Collision';

/**
 * Camera góc nhìn thứ ba: bám theo mục tiêu (có làm mượt), xoay yaw/pitch, zoom có giới hạn,
 * tránh xuyên vật thể (rút ngắn khi tia camera→nhân vật cắt cột/tượng) và không chui dưới mặt đất.
 */
export class ThirdPersonCamera {
  yaw = Math.PI; pitch = 0.32; distance = 7.5;
  minDist = 2.6; maxDist = 13; minPitch = -0.05; maxPitch = 1.15;
  /** Góc nhìn thứ nhất (như minefun / Minecraft): camera ở mắt nhân vật, nhìn theo chuột. */
  firstPerson = false;
  eyeHeight = 1.6;
  private readonly focus = new THREE.Vector3();
  private currentDist = 7.5;
  private readonly occluders: THREE.Object3D[] = [];
  private readonly ray = new THREE.Raycaster();

  constructor(private readonly cam: THREE.PerspectiveCamera, private heightAt: (x: number, z: number) => number = () => 0, private colliders: Collider[] = []) {}

  setWorld(heightAt: (x: number, z: number) => number, colliders: Collider[], occluders: THREE.Object3D[] = []) {
    this.heightAt = heightAt; this.colliders = colliders; this.occluders.length = 0; this.occluders.push(...occluders);
  }

  addOccluders(list: THREE.Object3D[]) { this.occluders.push(...list); }
  rotate(dx: number, dy: number) { this.yaw -= dx * 0.0055; this.pitch = this.firstPerson ? THREE.MathUtils.clamp(this.pitch + dy * 0.004, -1.35, 1.35) : THREE.MathUtils.clamp(this.pitch + dy * 0.004, this.minPitch, this.maxPitch); }
  setFirstPerson(on: boolean) { this.firstPerson = on; if (!on) this.pitch = THREE.MathUtils.clamp(this.pitch, this.minPitch, this.maxPitch); else this.pitch = THREE.MathUtils.clamp(this.pitch, -0.2, 0.2); }
  zoom(d: number) { this.distance = THREE.MathUtils.clamp(this.distance + d, this.minDist, this.maxDist); }

  snap(target: THREE.Vector3) { this.focus.copy(target).add(new THREE.Vector3(0, 1.45, 0)); this.currentDist = this.distance; this.place(1); }

  update(target: THREE.Vector3, dt: number) {
    if (this.firstPerson) { // mắt nhân vật, nhìn theo yaw/pitch (không làm mượt để chuột "ăn" ngay)
      this.focus.set(target.x, target.y + this.eyeHeight, target.z);
      const look = this.focus.clone().addScaledVector(this.dirVec(), -10);
      this.cam.position.copy(this.focus).addScaledVector(this.forward(), 0.12); this.cam.lookAt(look); return;
    }
    const desired = target.clone().add(new THREE.Vector3(0, 1.45, 0));
    this.focus.lerp(desired, 1 - Math.exp(-dt * 10));
    // khoảng cách mong muốn, rút ngắn nếu bị che
    let want = this.distance;
    const dir = this.dirVec();
    const camX = this.focus.x + dir.x * want, camZ = this.focus.z + dir.z * want;
    for (const c of this.colliders) {
      if (c.kind !== 'circle' || c.r < 0.5) continue;
      const t = segmentHitsCircle(this.focus.x, this.focus.z, camX, camZ, { ...c, r: c.r + 0.3 });
      if (t !== null) want = Math.min(want, Math.max(this.minDist * 0.6, want * t - 0.2));
    }
    if (this.occluders.length) {
      this.ray.set(this.focus, dir); this.ray.far = want;
      const hit = this.ray.intersectObjects(this.occluders, true)[0];
      if (hit) want = Math.max(this.minDist * 0.6, hit.distance - 0.3);
    }
    // nhanh khi bị che, chậm khi trả lại (tránh giật)
    this.currentDist += (want - this.currentDist) * (want < this.currentDist ? 1 - Math.exp(-dt * 20) : 1 - Math.exp(-dt * 4));
    this.place(dt);
  }

  private dirVec() { return new THREE.Vector3(Math.sin(this.yaw) * Math.cos(this.pitch), Math.sin(this.pitch), Math.cos(this.yaw) * Math.cos(this.pitch)).normalize(); }

  private place(_dt: number) {
    const p = this.focus.clone().addScaledVector(this.dirVec(), this.currentDist);
    const ground = this.heightAt(p.x, p.z) + 0.4; // tự điều chỉnh theo địa hình
    if (p.y < ground) p.y = ground;
    this.cam.position.copy(p); this.cam.lookAt(this.focus);
  }

  /** Hướng "tiến" trên mặt đất theo camera – dùng để chuyển input sang thế giới. */
  forward() { return new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw)); }
}
